import {
  Text,
  Image,
  Flex,
  Box,
  Spinner,
  Button,
  Link,
} from '@chakra-ui/react';
import { LOGO_BASE64 } from '../../../utils/base64';
import PassDownloadTemplateContext from '../../../store/client/PassDownloadTemplateContext';
import { useContext } from 'react';
import footerLight from '../../../assets/vectors/footer-light.svg';
import footerDark from '../../../assets/vectors/footer-dark.svg';
import { downloadPageTranslations } from '../../../utils/downloadPageTranslations';
import { CustomHelpIcon } from '../../../theme/icons/CustomHelpIcon';

const PreviewMobileScreen = (props) => {
  const { type, device, editingLang } = props;
  const passDownloadCtx = useContext(PassDownloadTemplateContext);
  const t =
    downloadPageTranslations[editingLang] || downloadPageTranslations.en;
  return (
    <Flex
      data-testid="preview-mobile-screen"
      direction="column"
      align="center"
      w="100%"
      mt={3}
    >
      <Flex
        data-testid="mobile-background"
        minH={{ base: '600px', md: '700px' }}
        w={{ base: '320px', md: '375px' }}
        bg={passDownloadCtx.backgroundColor}
        border="1px solid #ccc"
        boxShadow="0px 6px 13px rgb(0 0 0 / 15%)"
        borderRadius="15px"
        direction="column"
        position="relative"
        alignItems="center"
        justify="center"
        px={{ base: 4, md: 6 }}
        pt={{ base: 6, md: 8 }}
        pb="70px"
      >
        {/* Header Text */}
        {type !== 'failure' &&
          type !== 'success' &&
          passDownloadCtx.headerText && (
            <Text
              data-testid="header-text"
              fontFamily="avenir"
              textStyle="headingFamilyMedium"
              fontSize={{ base: '18px', md: '20px' }}
              fontWeight="600"
              color={passDownloadCtx.headerColor}
              textAlign="center"
              mb={8}
            >
              {editingLang
                ? passDownloadCtx.translations[editingLang].headerText
                : passDownloadCtx.headerText}
            </Text>
          )}
        {/* Info Card */}
        <Box
          data-testid="download-info-card"
          bg="#FFFFFF"
          borderRadius="15px"
          w={{ base: '250px', md: '300px' }}
          py="30px"
          px={{ base: 4, md: 8 }}
        >
          <Flex direction="column" align="center" justify="center" gap={4}>
            <Image
              data-testid="default-logo-image"
              maxW="100%"
              w={{ base: '150px', md: '200px' }}
              src={passDownloadCtx.logoImage || LOGO_BASE64}
              alt="pass-download-web-page"
            />
            {/* Pass download Text */}
            {type !== 'failure' &&
              type !== 'success' &&
              passDownloadCtx.passDownloadText && (
                <Text
                  data-testid="body-text"
                  fontFamily="avenir"
                  fontSize={{ base: '14px', md: '16px' }}
                  color={passDownloadCtx.passDownloadTextColor}
                  textAlign="center"
                >
                  {editingLang
                    ? passDownloadCtx.translations[editingLang].passDownloadText
                    : passDownloadCtx.passDownloadText}
                </Text>
              )}
            {/* Add to Wallet */}
            {type === 'addToWallet' && (
              <>
                {device === 'Google' ? (
                  <Image
                    data-testid="add-to-google-wallet-button"
                    w="130px"
                    src={`/google_wallet_buttons/${editingLang || 'en'}/add_to_wallet.svg`}
                    alt="pass-download-web-page"
                  />
                ) : (
                  <Image
                    data-testid="add-to-apple-wallet-button"
                    w="130px"
                    src={`/apple_wallet_buttons/${editingLang || 'en'}/add_to_wallet.svg`}
                    alt="pass-download-web-page"
                  />
                )}
              </>
            )}
            {/* Building your pass */}
            {type === 'building' && (
              <Flex direction="column" alignItems="center" w="100%">
                <Spinner
                  data-testid="spinner"
                  speed="3s"
                  borderWidth="6px"
                  color={passDownloadCtx.componentFillColor}
                  emptyColor="gray.200"
                />
                <Text
                  textAlign="center"
                  fontFamily="avenir"
                  fontSize={{ base: '14px', md: '16px' }}
                  color={passDownloadCtx.passDownloadTextColor || 'gray'}
                  mt={3}
                >
                  {t.building}
                </Text>
              </Flex>
            )}
            {/* Success */}
            {type === 'success' && (
              <>
                <Text
                  fontFamily="avenir"
                  color={passDownloadCtx.passDownloadTextColor || '#45525B'}
                  fontSize={{ base: '14px', md: '16px' }}
                  textAlign="center"
                  w="100%"
                  mt={2}
                  lineHeight="20px"
                >
                  {editingLang
                    ? passDownloadCtx?.translations[editingLang]?.successMessage
                    : passDownloadCtx.successMessage}
                </Text>
                {device === 'Google' ? (
                  <Button
                    data-testid="go-to-google-wallet-button"
                    size="md"
                    w={{ base: '180px', md: 'auto' }}
                    px={{ base: 'auto', md: '6' }}
                    fontSize="14px"
                    borderRadius="12px"
                    bg={passDownloadCtx.componentFillColor}
                    color={passDownloadCtx.componentFontColor}
                    _hover={{
                      bg: passDownloadCtx.componentFillColor,
                      color: passDownloadCtx.componentFontColor,
                      opacity: 0.9,
                    }}
                    mt={1}
                  >
                    {t.goToWalletGoogle}
                  </Button>
                ) : (
                  <Button
                    data-testid="go-to-apple-wallet-button"
                    size="md"
                    w={{ base: '180px', md: 'auto' }}
                    px={{ base: 'auto', md: '6' }}
                    fontSize="14px"
                    borderRadius="12px"
                    bg={passDownloadCtx.componentFillColor}
                    color={passDownloadCtx.componentFontColor}
                    _hover={{
                      bg: passDownloadCtx.componentFillColor,
                      color: passDownloadCtx.componentFontColor,
                      opacity: 0.9,
                    }}
                    mt={1}
                  >
                    {t.goToWalletApple}
                  </Button>
                )}
                <Flex direction="column" align="center" gap={2}>
                  <Flex
                    direction="row"
                    align="center"
                    gap={1}
                    wrap="wrap"
                    w="100%"
                    justify="center"
                  >
                    <Text
                      fontFamily="avenir"
                      fontSize="14px"
                      color={passDownloadCtx.passDownloadTextColor || '#45525B'}
                    >
                      {t.facingIssues}
                    </Text>
                    <Link
                      color={passDownloadCtx.passDownloadTextColor || '#45525B'}
                      fontSize="12px"
                      fontWeight="bold"
                      href="#"
                      position="relative"
                      _hover={{ textDecoration: 'none' }}
                    >
                      {t.tryAgain}
                      <Box
                        position="absolute"
                        bottom="-3px"
                        left="0"
                        w="100%"
                        h="2px"
                        bg={passDownloadCtx.componentFillColor}
                      />
                    </Link>
                  </Flex>
                  {/* Help and FAQs link */}
                  {passDownloadCtx.clientHelpLink?.url &&
                    passDownloadCtx.clientHelpLink?.display_text !== '' && (
                      <Flex
                        direction="row"
                        align="center"
                        gap={1}
                        mt={2}
                        _hover={{
                          textDecoration: 'underline',
                          textDecorationColor:
                            passDownloadCtx.passDownloadTextColor || '#45525B',
                        }}
                      >
                        <CustomHelpIcon
                          boxSize={'10px'}
                          color={passDownloadCtx.componentFillColor}
                        />
                        <Link
                          color={
                            passDownloadCtx.passDownloadTextColor || '#45525B'
                          }
                          fontSize="11px"
                          href="#"
                          position="relative"
                        >
                          <Text fontFamily="avenir" fontWeight="bold">
                            {editingLang
                              ? passDownloadCtx.translations[editingLang]
                                  .clientHelpLink
                              : passDownloadCtx.clientHelpLink?.display_text}
                          </Text>
                        </Link>
                      </Flex>
                    )}
                </Flex>
              </>
            )}
            {/* Failure */}
            {type === 'failure' && (
              <Text
                fontFamily="avenir"
                color={passDownloadCtx.passDownloadTextColor || '#45525B'}
                fontSize={{ base: '14px', md: '16px' }}
                textAlign="center"
                w="100%"
              >
                {device === 'Google'
                  ? t.alreadyAddedGoogle
                  : t.alreadyAddedApple}
              </Text>
            )}
            {/* Help and FAQs link */}
            {type !== 'success' &&
              passDownloadCtx.clientHelpLink?.url &&
              passDownloadCtx.clientHelpLink?.display_text !== '' && (
                <Flex
                  direction="row"
                  align="center"
                  gap={1}
                  _hover={{
                    textDecoration: 'underline',
                    textDecorationColor:
                      passDownloadCtx.passDownloadTextColor || '#45525B',
                  }}
                >
                  <CustomHelpIcon
                    boxSize={'10px'}
                    color={passDownloadCtx.componentFillColor}
                  />
                  <Link
                    color={passDownloadCtx.passDownloadTextColor || '#45525B'}
                    fontSize="11px"
                    href="#"
                    position="relative"
                  >
                    <Text fontFamily="avenir" fontWeight="bold">
                      {editingLang
                        ? passDownloadCtx.translations[editingLang]
                            .clientHelpLink
                        : passDownloadCtx.clientHelpLink?.display_text}
                    </Text>
                  </Link>
                </Flex>
              )}
          </Flex>
        </Box>
        {/* Footer */}
        {!passDownloadCtx.skipFooter && (
          <Box data-testid="passentry-footer" position="absolute" bottom="30px">
            <Image
              width="150px"
              src={
                passDownloadCtx.footerColor === 'dark'
                  ? footerDark
                  : footerLight
              }
              alt="pass-download-web-page"
            />
          </Box>
        )}
      </Flex>
    </Flex>
  );
};

export default PreviewMobileScreen;
